import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { NavItem } from '../../models';

/**
 * Bottom navigation bar for narrow viewports.
 *
 * A bottom bar can only hold a handful of destinations before its labels
 * collide, so this component renders a bounded number of them and relies on the
 * pinned overflow action — the "Menu" item every subsite appends, which opens
 * the full side navigation — for everything else.
 *
 * Two rules make that safe:
 *
 * 1. Items carrying an `action` with no navigable `link` are treated as pinned
 *    triggers and are *always* rendered, in the last slots. Previously they were
 *    appended like any other item, so on a subsite with ten destinations the
 *    Menu button was pushed past the edge of the screen and could not be reached
 *    at all — the bar showed ten crushed labels and no way to open the menu.
 * 2. Everything else competes for the remaining slots in order. Items that do
 *    not fit are not shown here; they remain reachable through the menu, which
 *    is the reason the menu is pinned.
 */
@Component({
  standalone: false,
  selector: 'sub-mobile-footer',
  templateUrl: './mobile-footer.component.html',
  styleUrls: ['./mobile-footer.component.scss'],
})
export class MobileFooterComponent implements OnChanges {
  @Input() navItems: NavItem[] = [];

  /**
   * Total slots in the bar, including the pinned menu. Five is the practical
   * ceiling on a 360px screen once each slot holds an icon and a legible label.
   */
  @Input() maxVisibleItems = 5;

  @Output() itemClicked = new EventEmitter<void>();

  /** Destinations that fit, in order. */
  visibleItems: NavItem[] = [];

  /** Always-rendered triggers, such as the menu toggle. */
  pinnedItems: NavItem[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navItems'] || changes['maxVisibleItems']) {
      this.partition();
    }
  }

  private partition(): void {
    const items = (this.navItems ?? []).filter((item) => !item.isHidden);

    // A pinned trigger opens something rather than navigating: it has an action
    // and no route to go to. Every subsite's "Menu" item is shaped this way.
    this.pinnedItems = items.filter((item) => !this.isNavigable(item) && !!item.action);

    const destinations = items.filter((item) => this.isNavigable(item));
    const slots = Math.max(1, this.maxVisibleItems) - this.pinnedItems.length;
    this.visibleItems = slots > 0 ? destinations.slice(0, slots) : [];
  }

  private isNavigable(item: NavItem): boolean {
    return !!item.link && item.link.trim().length > 0;
  }

  onItemClick(item: NavItem): void {
    if (item.action) {
      item.action();
    }

    this.itemClicked.emit();

    if (item.external) {
      window.location.href = item.link;
    }
  }
}
